import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { 
    fetchUserAnalytics, 
    fetchRecipeAnalytics, 
    fetchSustainabilityAnalytics,
    fetchPerformanceAnalytics 
} from '../../store/slices/adminReducer';
import AdminSidebar from '../../components/Admin/AdminSidebar';
import AdminHeader from '../../components/Admin/AdminHeader';
import StatsCard from '../../components/Admin/StatsCard';

function Analytics() {
    const { auth } = useSelector((state) => state.affiliate);
    const { analytics } = useSelector((state) => state.admin);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [activeTab, setActiveTab] = useState('overview');
    const [dateRange, setDateRange] = useState('30');

    // Check if user is admin
    useEffect(() => {
        if (auth?.affiliate_role !== "admin") {
            navigate("/login");
        }
    }, [auth, navigate]);

    // Fetch analytics data
    useEffect(() => {
        if (auth?.affiliate_role === "admin") {
            dispatch(fetchUserAnalytics({ period: dateRange }));
            dispatch(fetchRecipeAnalytics({ period: dateRange }));
            dispatch(fetchSustainabilityAnalytics());
            dispatch(fetchPerformanceAnalytics());
        }
    }, [dispatch, auth, dateRange]);

    const tabs = [
        { id: 'overview', label: 'Overview', icon: '📊' },
        { id: 'users', label: 'Users', icon: '👥' },
        { id: 'recipes', label: 'Recipes', icon: '🍽️' },
        { id: 'sustainability', label: 'Sustainability', icon: '🌱' },
        { id: 'performance', label: 'Performance', icon: '⚡' }
    ];

    const renderOverview = () => {
        const userData = analytics?.users || {};
        const recipeData = analytics?.recipes || {};
        const sustainabilityData = analytics?.sustainability || {};

        return (
            <div className="space-y-6">
                {/* Key Metrics */}
                <div className="grid grid-cols-4 mobile:grid-cols-1 tablet:grid-cols-2 laptop:grid-cols-2 desktop:grid-cols-4 gap-6">
                    <StatsCard
                        title="Total Users"
                        value={userData?.overview?.total_users || 0}
                        change={userData?.overview?.new_users || 0}
                        changeType="positive"
                        icon="👥"
                        color="blue"
                        loading={analytics.loading}
                    />
                    <StatsCard
                        title="Active Users"
                        value={userData?.overview?.active_users || 0}
                        change={5.2}
                        changeType="positive"
                        icon="✅"
                        color="green"
                        loading={analytics.loading}
                    />
                    <StatsCard
                        title="Total Recipes"
                        value={recipeData?.overview?.total_recipes || 0}
                        change={12.5}
                        changeType="positive"
                        icon="🍽️"
                        color="purple"
                        loading={analytics.loading}
                    />
                    <StatsCard
                        title="Avg Sustainability"
                        value={`${sustainabilityData?.overview?.average_co2e || 0}%`}
                        change={-2.1}
                        changeType="positive"
                        icon="🌱"
                        color="green"
                        loading={analytics.loading}
                    />
                </div>

                {/* Charts Section */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* User Growth Chart */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">User Growth</h3>
                        <div className="h-64 flex items-center justify-center text-gray-500">
                            <div className="text-center">
                                <div className="text-4xl mb-2">📈</div>
                                <p>User growth chart would be here</p>
                                <p className="text-sm">Integration with charting library needed</p>
                            </div>
                        </div>
                    </div>

                    {/* Recipe Categories */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Recipe Categories</h3>
                        <div className="space-y-3">
                            {recipeData?.categories?.slice(0, 5).map((category, index) => (
                                <div key={index} className="flex items-center justify-between">
                                    <span className="text-sm text-gray-600">{category.name}</span>
                                    <div className="flex items-center space-x-2">
                                        <div className="w-20 bg-gray-200 rounded-full h-2">
                                            <div 
                                                className="bg-blue-600 h-2 rounded-full" 
                                                style={{ width: `${(category.count / (recipeData?.categories?.[0]?.count || 1)) * 100}%` }}
                                            ></div>
                                        </div>
                                        <span className="text-sm font-medium text-gray-900">{category.count}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const renderUsers = () => {
        const userData = analytics?.users || {};

        return (
            <div className="space-y-6">
                {/* User Analytics Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <StatsCard
                        title="New Users (30d)"
                        value={userData?.overview?.new_users || 0}
                        change={15.3}
                        changeType="positive"
                        icon="🆕"
                        color="blue"
                        loading={analytics.loading}
                    />
                    <StatsCard
                        title="Active Users"
                        value={userData?.overview?.active_users || 0}
                        change={8.7}
                        changeType="positive"
                        icon="👤"
                        color="green"
                        loading={analytics.loading}
                    />
                    <StatsCard
                        title="Admin Users"
                        value={userData?.overview?.admin_users || 0}
                        change={0}
                        changeType="neutral"
                        icon="👑"
                        color="purple"
                        loading={analytics.loading}
                    />
                </div>

                {/* User Growth Chart */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">User Growth Over Time</h3>
                    <div className="h-64 flex items-center justify-center text-gray-500">
                        <div className="text-center">
                            <div className="text-4xl mb-2">📊</div>
                            <p>User growth chart would be here</p>
                        </div>
                    </div>
                </div>

                {/* User Engagement */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">User Engagement</h3>
                        <div className="space-y-4">
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-gray-600">Daily Active Users</span>
                                <span className="font-medium">{userData?.engagement?.daily_active_users || 0}</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-gray-600">Weekly Active Users</span>
                                <span className="font-medium">{userData?.engagement?.weekly_active_users || 0}</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-gray-600">Monthly Active Users</span>
                                <span className="font-medium">{userData?.engagement?.monthly_active_users || 0}</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-gray-600">Retention Rate</span>
                                <span className="font-medium">{userData?.engagement?.retention_rate || 0}%</span>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">User Activity</h3>
                        <div className="space-y-4">
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-gray-600">Login Attempts</span>
                                <span className="font-medium">{userData?.activity?.login_attempts || 0}</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-gray-600">Active Sessions</span>
                                <span className="font-medium">{userData?.activity?.active_sessions || 0}</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-gray-600">Page Views</span>
                                <span className="font-medium">{userData?.activity?.page_views || 0}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const renderRecipes = () => {
        const recipeData = analytics?.recipes || {};

        return (
            <div className="space-y-6">
                {/* Recipe Analytics Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <StatsCard
                        title="Total Recipes"
                        value={recipeData?.overview?.total_recipes || 0}
                        change={12.5}
                        changeType="positive"
                        icon="🍽️"
                        color="blue"
                        loading={analytics.loading}
                    />
                    <StatsCard
                        title="Published Recipes"
                        value={recipeData?.overview?.published_recipes || 0}
                        change={8.2}
                        changeType="positive"
                        icon="✅"
                        color="green"
                        loading={analytics.loading}
                    />
                    <StatsCard
                        title="Draft Recipes"
                        value={recipeData?.overview?.draft_recipes || 0}
                        change={-3.1}
                        changeType="negative"
                        icon="📝"
                        color="yellow"
                        loading={analytics.loading}
                    />
                </div>

                {/* Recipe Trends */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Recipe Creation Trends</h3>
                    <div className="h-64 flex items-center justify-center text-gray-500">
                        <div className="text-center">
                            <div className="text-4xl mb-2">📈</div>
                            <p>Recipe trends chart would be here</p>
                        </div>
                    </div>
                </div>

                {/* Popular Recipes */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Most Popular Recipes</h3>
                    <div className="space-y-3">
                        {recipeData?.popular?.slice(0, 5).map((recipe, index) => (
                            <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                <div>
                                    <p className="font-medium text-gray-900">{recipe.name}</p>
                                    <p className="text-sm text-gray-500">Created {new Date(recipe.created_at).toLocaleDateString()}</p>
                                </div>
                                <span className="text-sm font-medium text-gray-600">#{index + 1}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    };

    const renderSustainability = () => {
        const sustainabilityData = analytics?.sustainability || {};

        return (
            <div className="space-y-6">
                {/* Sustainability Metrics */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <StatsCard
                        title="Avg CO2e Score"
                        value={sustainabilityData?.overview?.average_co2e || 0}
                        change={-5.2}
                        changeType="positive"
                        icon="🌍"
                        color="green"
                        loading={analytics.loading}
                    />
                    <StatsCard
                        title="Avg Water Score"
                        value={sustainabilityData?.overview?.average_water || 0}
                        change={3.1}
                        changeType="positive"
                        icon="💧"
                        color="blue"
                        loading={analytics.loading}
                    />
                    <StatsCard
                        title="Avg Health Score"
                        value={sustainabilityData?.overview?.average_health || 0}
                        change={7.8}
                        changeType="positive"
                        icon="❤️"
                        color="red"
                        loading={analytics.loading}
                    />
                    <StatsCard
                        title="Avg Social Score"
                        value={sustainabilityData?.overview?.average_social || 0}
                        change={2.3}
                        changeType="positive"
                        icon="🤝"
                        color="purple"
                        loading={analytics.loading}
                    />
                </div>

                {/* Sustainability Distribution */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Sustainability Distribution</h3>
                        <div className="space-y-3">
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-gray-600">Excellent (80%+)</span>
                                <span className="font-medium">{sustainabilityData?.distribution?.excellent || 0}</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-gray-600">Good (60-79%)</span>
                                <span className="font-medium">{sustainabilityData?.distribution?.good || 0}</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-gray-600">Fair (40-59%)</span>
                                <span className="font-medium">{sustainabilityData?.distribution?.fair || 0}</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-gray-600">Poor (&lt;40%)</span>
                                <span className="font-medium">{sustainabilityData?.distribution?.poor || 0}</span>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">Sustainability Benchmarks</h3>
                        <div className="space-y-4">
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-gray-600">Industry Average</span>
                                <span className="font-medium">{sustainabilityData?.benchmarks?.industry_average || 0}%</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-gray-600">Best Practice</span>
                                <span className="font-medium">{sustainabilityData?.benchmarks?.best_practice || 0}%</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-gray-600">Our Average</span>
                                <span className="font-medium">{sustainabilityData?.benchmarks?.our_average || 0}%</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const renderPerformance = () => {
        const performanceData = analytics?.performance || {};

        return (
            <div className="space-y-6">
                {/* Performance Metrics */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <StatsCard
                        title="Response Time"
                        value={`${performanceData?.system?.response_time || 0}ms`}
                        change={-12.5}
                        changeType="positive"
                        icon="⚡"
                        color="green"
                        loading={analytics.loading}
                    />
                    <StatsCard
                        title="Error Rate"
                        value={`${performanceData?.system?.error_rate || 0}%`}
                        change={-0.3}
                        changeType="positive"
                        icon="❌"
                        color="red"
                        loading={analytics.loading}
                    />
                    <StatsCard
                        title="Uptime"
                        value={`${performanceData?.system?.uptime || 0}%`}
                        change={0.1}
                        changeType="positive"
                        icon="✅"
                        color="blue"
                        loading={analytics.loading}
                    />
                    <StatsCard
                        title="Cache Hit Rate"
                        value={`${performanceData?.cache?.hit_rate || 0}%`}
                        change={2.1}
                        changeType="positive"
                        icon="💾"
                        color="purple"
                        loading={analytics.loading}
                    />
                </div>

                {/* Performance Charts */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">System Performance</h3>
                        <div className="h-64 flex items-center justify-center text-gray-500">
                            <div className="text-center">
                                <div className="text-4xl mb-2">📊</div>
                                <p>Performance chart would be here</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                        <h3 className="text-lg font-semibold text-gray-900 mb-4">API Usage</h3>
                        <div className="space-y-3">
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-gray-600">Requests per minute</span>
                                <span className="font-medium">{performanceData?.api?.requests_per_minute || 0}</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-gray-600">Popular endpoints</span>
                                <span className="font-medium">{performanceData?.api?.popular_endpoints?.length || 0}</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-gray-600">Error endpoints</span>
                                <span className="font-medium">{performanceData?.api?.error_endpoints?.length || 0}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    if (auth?.affiliate_role !== "admin") {
        return null;
    }

    return (
        <div className="min-h-screen bg-gray-100 flex">
            {/* Sidebar */}
            <AdminSidebar 
                collapsed={sidebarCollapsed} 
                onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} 
            />

            {/* Main Content */}
            <div className="flex-1 flex flex-col">
                {/* Header */}
                <AdminHeader 
                    onToggleSidebar={() => setSidebarCollapsed(!sidebarCollapsed)}
                    sidebarCollapsed={sidebarCollapsed}
                />

                {/* Content */}
                <div className="flex-1 p-6">
                    {/* Page Header */}
                    <div className="mb-8">
                        <h1 className="text-3xl font-bold text-gray-900 mb-2">Analytics</h1>
                        <p className="text-gray-600">
                            Comprehensive analytics and insights for your system.
                        </p>
                    </div>

                    {/* Date Range Selector */}
                    <div className="mb-6">
                        <div className="flex items-center space-x-4">
                            <label className="text-sm font-medium text-gray-700">Date Range:</label>
                            <select
                                value={dateRange}
                                onChange={(e) => setDateRange(e.target.value)}
                                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                <option value="7">Last 7 days</option>
                                <option value="30">Last 30 days</option>
                                <option value="90">Last 90 days</option>
                                <option value="365">Last year</option>
                            </select>
                        </div>
                    </div>

                    {/* Tabs */}
                    <div className="mb-6">
                        <div className="border-b border-gray-200">
                            <nav className="-mb-px flex space-x-8">
                                {tabs.map((tab) => (
                                    <button
                                        key={tab.id}
                                        onClick={() => setActiveTab(tab.id)}
                                        className={`py-2 px-1 border-b-2 font-medium text-sm ${
                                            activeTab === tab.id
                                                ? 'border-blue-500 text-blue-600'
                                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                        }`}
                                    >
                                        <span className="mr-2">{tab.icon}</span>
                                        {tab.label}
                                    </button>
                                ))}
                            </nav>
                        </div>
                    </div>

                    {/* Tab Content */}
                    <div>
                        {activeTab === 'overview' && renderOverview()}
                        {activeTab === 'users' && renderUsers()}
                        {activeTab === 'recipes' && renderRecipes()}
                        {activeTab === 'sustainability' && renderSustainability()}
                        {activeTab === 'performance' && renderPerformance()}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Analytics;
